import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { api } from "@/lib/api";
import { CalendarPlus, Loader2, Search } from "lucide-react";
import { toast } from "sonner";

// Owner-scoped manual meeting creation. Reuses canonical `meetings` collection
// via POST /api/admin/meetings. Timestamps entered in workspace-local TZ,
// serialised to UTC ISO before submit.
export function MeetingFormDialog({ open, onClose, onCreated, defaultLead = null }) {
  const [busy, setBusy] = useState(false);
  const [cards, setCards] = useState([]);
  const [meetingTypes, setMeetingTypes] = useState([]);
  const [leadSearch, setLeadSearch] = useState("");
  const [leadResults, setLeadResults] = useState([]);
  const [f, setF] = useState({
    card_id: "", lead_id: defaultLead?.id || null, lead_label: defaultLead ? `${defaultLead.name}${defaultLead.email ? ` <${defaultLead.email}>` : ""}` : "",
    date: "", time: "", duration: 30, meeting_type_id: "",
    location: "", notes: "", status: "confirmed",
  });

  useEffect(() => {
    if (!open) return;
    api.get("/admin/cards").then(({ data }) => {
      const list = (data || []).filter((c) => c.status === "published" || c.status === "active");
      setCards(list);
      if (!f.card_id && list[0]) {
        setF((s) => ({ ...s, card_id: list[0].id }));
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!f.card_id) return;
    api.get(`/admin/cards/${f.card_id}/meeting-types`).then(({ data }) => setMeetingTypes(data || [])).catch(() => setMeetingTypes([]));
  }, [f.card_id]);

  useEffect(() => {
    if (!leadSearch.trim() || leadSearch.trim().length < 2) { setLeadResults([]); return; }
    const t = setTimeout(() => {
      api.get("/crm/leads", { params: { q: leadSearch } }).then(({ data }) => setLeadResults((data || []).slice(0, 6))).catch(() => setLeadResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [leadSearch]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!f.card_id) { toast.error("Choose a card"); return; }
    if (!f.date || !f.time) { toast.error("Date and time are required"); return; }
    // Local input → UTC ISO. `new Date("YYYY-MM-DDTHH:mm")` interprets in browser TZ.
    const local = new Date(`${f.date}T${f.time}`);
    if (isNaN(local.getTime())) { toast.error("Invalid date/time"); return; }
    setBusy(true);
    try {
      const payload = {
        card_id: f.card_id,
        lead_id: f.lead_id || null,
        meeting_type_id: f.meeting_type_id || null,
        start_utc: local.toISOString(),
        duration: Number(f.duration) || 30,
        location: f.location,
        notes: f.notes,
        status: f.status,
      };
      const { data } = await api.post("/admin/meetings", payload);
      toast.success("Meeting created");
      onCreated?.(data);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create meeting");
    } finally { setBusy(false); }
  };

  const inputCls = "w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white/90 outline-none focus:border-[#D6A653]/70 focus:ring-1 focus:ring-[#D6A653]/40";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl bg-[#0A0B0F] text-white border border-white/10">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-light tracking-tight">
            <CalendarPlus className="h-4 w-4 text-[#D6A653]" /> New meeting
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {cards.length > 1 && (
            <Select value={f.card_id} onValueChange={(v) => setF((s) => ({ ...s, card_id: v }))}>
              <SelectTrigger className="rounded-lg border border-white/10 bg-black/40 text-sm text-white/90" data-testid="meeting-form-card">
                <SelectValue placeholder="Card" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0B0F] text-white/90 border-white/10">
                {cards.map((c) => <SelectItem key={c.id} value={c.id}>/{c.slug}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <div>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
              <input
                value={f.lead_id ? f.lead_label : leadSearch}
                onChange={(e) => { setLeadSearch(e.target.value); if (f.lead_id) setF((s) => ({ ...s, lead_id: null, lead_label: "" })); }}
                placeholder="Attach a lead (search name / email)"
                className={`${inputCls} pl-9`}
                data-testid="meeting-form-lead-search"
              />
            </div>
            {leadResults.length > 0 && !f.lead_id && (
              <div className="mt-1 max-h-40 overflow-auto rounded-lg border border-white/10 bg-[#0A0B0F]">
                {leadResults.map((l) => (
                  <button type="button" key={l.id} onClick={() => { setF((s) => ({ ...s, lead_id: l.id, lead_label: `${l.name}${l.email ? ` <${l.email}>` : ""}` })); setLeadResults([]); setLeadSearch(""); }} className="block w-full px-3 py-2 text-left text-xs text-white/80 hover:bg-white/5" data-testid={`meeting-form-lead-${l.id}`}>
                    {l.name} {l.email ? <span className="text-white/45">· {l.email}</span> : null}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <input type="date" value={f.date} onChange={(e) => setF((s) => ({ ...s, date: e.target.value }))} className={inputCls} data-testid="meeting-form-date" />
            <input type="time" value={f.time} onChange={(e) => setF((s) => ({ ...s, time: e.target.value }))} className={inputCls} data-testid="meeting-form-time" />
            <input type="number" min="5" max="480" step="5" value={f.duration} onChange={(e) => setF((s) => ({ ...s, duration: e.target.value }))} className={inputCls} data-testid="meeting-form-duration" />
          </div>
          {meetingTypes.length > 0 && (
            <Select value={f.meeting_type_id || "__none"} onValueChange={(v) => setF((s) => ({ ...s, meeting_type_id: v === "__none" ? "" : v }))}>
              <SelectTrigger className="rounded-lg border border-white/10 bg-black/40 text-sm text-white/90" data-testid="meeting-form-type">
                <SelectValue placeholder="Meeting type" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0B0F] text-white/90 border-white/10">
                <SelectItem value="__none">— None —</SelectItem>
                {meetingTypes.map((mt) => <SelectItem key={mt.id} value={mt.id}>{mt.title} · {mt.duration_min}m</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <input value={f.location} onChange={(e) => setF((s) => ({ ...s, location: e.target.value }))} placeholder="Location (address or meeting link)" className={inputCls} data-testid="meeting-form-location" />
          <textarea value={f.notes} onChange={(e) => setF((s) => ({ ...s, notes: e.target.value }))} placeholder="Notes" rows={3} className={inputCls} data-testid="meeting-form-notes" />
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={() => onClose?.()} className="rounded-lg border border-white/10 px-4 py-2 text-sm text-white/70 hover:bg-white/5" data-testid="meeting-form-cancel">Cancel</button>
            <button type="submit" disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-[#D6A653] px-4 py-2 text-sm font-medium text-black hover:brightness-110 disabled:opacity-50" data-testid="meeting-form-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create meeting
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
